'use client';

import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { DataTestConfig, PRODUCT_NOTIFY_ME } from '@headless-workspace/config';
import { ContentAssetKeys, unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useToasts, useUIContext } from '@headless-workspace/core-ui';
import {
    GlowButton,
    GlowClickable,
    GlowDivider,
    GlowIcon,
    GlowSkeleton,
    GlowTextBody,
    GlowTitle,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { getConnectedUserInfo, HTMLString } from '../../../server';
import { InnerHTML } from '../innerHTML';
import { useUserInfo } from '../user';
import { NotifyMeForm } from './NotifyMeForm';
import { useNotifyMeAssets } from './useNotifyMeAssets';

type NotifyMeFormProps = PropsWithLocale & {
    productIds: string[];
};

export const NotifyMeModal: Component<NotifyMeFormProps> = ({ productIds, locale }) => {
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const t = useTranslations('Discover.subscribe.notifyMe');
    const { result: userInfoResult } = useUserInfo();
    const userInfo = getConnectedUserInfo(userInfoResult);
    const { notifyError } = useToasts();

    const {
        menuModal: { close: closeMenu },
    } = useUIContext();

    const closeNotifyMeModal = useCallback(() => {
        closeMenu(PRODUCT_NOTIFY_ME);
    }, [closeMenu]);

    useEffect(() => {
        setIsSubmitted(false);
    }, [productIds]);

    const { isLoading, result: notifyMeAssetsResult } = useNotifyMeAssets();
    const notifyMeContentAssets = notifyMeAssetsResult ? unwrapResultSuccess(notifyMeAssetsResult) : undefined;

    useEffect(() => {
        if (notifyMeAssetsResult.type === 'failure') {
            notifyError(notifyMeAssetsResult.meta.message);
        }
    }, [notifyMeAssetsResult, notifyError]);

    if (!notifyMeContentAssets) {
        return null;
    }

    const notifyMeAssets: { [key: string]: HTMLString } | undefined = notifyMeContentAssets.reduce(
        (acc, asset) => Object.assign(acc, { [asset.id]: asset.content.html }),
        {},
    );

    const notifyMeTexts = notifyMeAssets
        ? {
              header: notifyMeAssets[ContentAssetKeys.NotifyMeHeaderText],
              chartProtect: notifyMeAssets[ContentAssetKeys.NotifyMeChartProtectText],
              confirmation: notifyMeAssets[ContentAssetKeys.NotifyMeConfirmationText],
          }
        : undefined;

    return (
        <div
            id={PRODUCT_NOTIFY_ME}
            className={'h-fullViewport w-fullViewport tablet:w-sideMenuTablet flex flex-col bg-background-l3'}
        >
            <div
                className={
                    'flex flex-row justify-between items-center p-1 gap-1 border-b-medium border-b-border-subtle border-solid h-headerFixedNavDesktop'
                }
            >
                <GlowTitle text={t('modal.label.title')} fontSize={'title4'} className={'flex-1 text-left'} />
                <GlowClickable
                    onClick={closeNotifyMeModal}
                    content={{
                        icon: <GlowIcon Icon={Icons.Cross} type={'large'} className={'text-text-primary'} />,
                    }}
                />
            </div>
            <NotifyMeModalContent
                productIds={productIds}
                locale={locale}
                isLoading={isLoading}
                isSubmitted={isSubmitted}
                setIsSubmitted={setIsSubmitted}
                notifyMeTexts={notifyMeTexts}
                userEmail={userInfo?.email}
                closeModal={{
                    label: t('modal.action.close'),
                    action: closeNotifyMeModal,
                }}
            />
        </div>
    );
};

type NotifyMeModalContentProps = NotifyMeFormProps & {
    isLoading: boolean;
    isSubmitted: boolean;
    setIsSubmitted: Dispatch<SetStateAction<boolean>>;
    notifyMeTexts?: {
        header: HTMLString;
        chartProtect: HTMLString;
        confirmation: HTMLString;
    };
    userEmail?: string;
    closeModal: {
        label: string;
        action: () => void;
    };
};

const NotifyMeModalContent: Component<NotifyMeModalContentProps> = ({
    productIds,
    locale,
    isLoading,
    isSubmitted,
    setIsSubmitted,
    notifyMeTexts,
    userEmail,
    closeModal,
}) => {
    if (isLoading) {
        return (
            <div className={'flex flex-col gap-2 m-1'}>
                <GlowSkeleton height={'4rem'} />
                <GlowSkeleton height={'3rem'} />
                <GlowSkeleton height={'12rem'} />
            </div>
        );
    }

    if (!isSubmitted) {
        return (
            <>
                <GlowTextBody
                    innerHTML={<InnerHTML rawHtml={notifyMeTexts?.header} />}
                    className={'px-1 pt-1 pb-0.5'}
                />
                <NotifyMeForm
                    onSubmit={setIsSubmitted}
                    productIds={productIds}
                    chartProtectText={notifyMeTexts?.chartProtect}
                    locale={locale}
                    defaultEmail={userEmail}
                    onCloseModal={closeModal.action}
                />
            </>
        );
    }

    return (
        <div className={'flex flex-1 flex-col'}>
            <div
                className={'flex flex-1 flex-col gap-0.5 p-1'}
                data-testid={DataTestConfig.testIds.notifyMeConfirmMessage}
            >
                <GlowIcon Icon={Icons.NotifyMe} type={'xxLarge'} />
                <GlowTextBody innerHTML={<InnerHTML rawHtml={notifyMeTexts?.confirmation} />} />
            </div>
            <GlowDivider />
            <div className={'flex items-center justify-center p-1 flex-col gap-0.5'}>
                <GlowButton
                    className={'flex-1 w-full'}
                    label={closeModal.label}
                    variant={'primary'}
                    onClick={closeModal.action}
                />
            </div>
        </div>
    );
};
